import React, { useEffect, useRef } from 'react';

const LIFE_CHOICES = [
  { question: "A new city has opportunities waiting...", a: "Move to the new city", b: "Stay where you are" },
  { question: "A friend invites you on a spontaneous trip...", a: "Go on the adventure", b: "Stay and save money" },
  { question: "You get two job offers at the same time...", a: "Take the risky dream job", b: "Take the safe stable job" },
  { question: "Your closest friend is drifting away...", a: "Reach out and talk about it", b: "Let them go naturally" },
  { question: "Someone offers to mentor you in a new field...", a: "Accept and start learning", b: "Stick with what you know" },
  { question: "You can afford one big purchase this year...", a: "Travel somewhere new", b: "Invest in yourself" },
  { question: "A chance to go back to school appears...", a: "Go back to school", b: "Keep working full-time" },
  { question: "You discover a passion project idea...", a: "Pursue the passion project", b: "Focus on responsibilities" },
  { question: "A family member needs help far away...", a: "Move closer to family", b: "Support from a distance" },
  { question: "Your lease is up. Two options...", a: "Live alone, small place", b: "Share a bigger place" },
  { question: "A festival is happening this weekend...", a: "Go solo, meet new people", b: "Skip it, recharge at home" },
  { question: "You're offered a role abroad...", a: "Take the leap overseas", b: "Stay in your home country" },
  { question: "Social media is consuming your time...", a: "Take a long break from it", b: "Set boundaries, keep it" },
  { question: "An old friend resurfaces after years...", a: "Reconnect and catch up", b: "Leave the past behind" },
  { question: "Your career feels stagnant...", a: "Make a bold career switch", b: "Work harder where you are" },
  { question: "You get a chance to live off-grid for a year...", a: "Try the off-grid life", b: "Stay connected to the world" },
  { question: "Your health needs attention...", a: "Overhaul your lifestyle", b: "Make small gradual changes" },
  { question: "A community project needs volunteers...", a: "Commit your weekends", b: "Protect your free time" },
  { question: "Your schedule is full, but you feel strangely empty...", a: "Cancel the noise and make room", b: "Keep pushing and stay productive" },
  { question: "A high-paying path asks more of your peace...", a: "Take less money, more time", b: "Keep the salary and absorb the pressure" },
  { question: "Most of your stress comes from comparison...", a: "Focus on your values", b: "Stay in the race" },
  { question: "A conflict keeps replaying in your mind...", a: "Call and own your part", b: "Let time bury it" },
  { question: "Your home is full, and so is your head...", a: "Simplify what you own", b: "Keep everything just in case" },
  { question: "A younger version of you made a bad decision...", a: "Forgive yourself and move forward", b: "Keep punishing yourself" },
  { question: "You can spend this year building status or stability...", a: "Build a quieter, steadier life", b: "Chase recognition while you can" },
  { question: "Your values and your habits no longer match...", a: "Change the habit", b: "Rewrite the value" },
  { question: "An opportunity appears through luck and timing...", a: "Own what you do with it", b: "Treat it as fate" },
  { question: "Someone asks what really matters to you...", a: "Answer honestly", b: "Give the safe answer" },
  { question: "Your phone fills every empty moment...", a: "Protect silence and boredom", b: "Stay constantly stimulated" },
  { question: "You can solve a problem by changing yourself or controlling others...", a: "Change yourself first", b: "Push harder on everyone else" },
  { question: "A big life decision has no perfect answer...", a: "Choose and commit meaning to it", b: "Wait for certainty" },
  { question: "You feel trapped by the person you've been...", a: "Act like the person you want to become", b: "Assume your past decides you" },
  { question: "Your work is respected, but it doesn't feel like yours...", a: "Redefine success on your own terms", b: "Keep the respectable path" },
  { question: "A friend says you seem exhausted...", a: "Rest before you break", b: "Push through and recover later" },
  { question: "You can own your mistake or explain it away...", a: "Take responsibility and repair it", b: "Defend yourself and move on" },
  { question: "A quiet life suddenly sounds appealing...", a: "Trade excess for enough", b: "Keep adding more" },
  { question: "You can help someone and no one will know...", a: "Do it because it matters", b: "Save your energy for visible wins" },
  { question: "Your life feels overdetermined by obligations...", a: "Carve out one deliberate choice", b: "Accept that this is just how it is" },
  { question: "You realize freedom may be smaller than you imagined...", a: "Use it carefully anyway", b: "Stop pretending your choices matter" },
  { question: "Your habits were shaped long before today...", a: "Take ownership of the next step", b: "Blame the past and stay still" },
  { question: "You can live by comfort or by conviction...", a: "Choose conviction", b: "Choose comfort" },
  { question: "A simple routine could make life gentler...", a: "Protect the routine", b: "Keep every day open-ended" },
];

const LifePathsFlow = () => {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    if (!canvas || !overlay) return;

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const CY = H / 2;
    const birthX = 65;
    const maxX = W - 35;
    const futureZoneWidth = Math.max(280, Math.floor(W * 0.36));
    const presentMaxX = maxX - futureZoneWidth;
    const PATH_LINE_WIDTH = 3.4;
    const PREVIEW_LINE_WIDTH = 2.4;

    // --- Seeded random ---
    let seed = 42;
    function sr() {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    }

    function combineSegments(...segments) {
      const joined = [];
      segments.forEach((segment, index) => {
        if (!segment || segment.length === 0) return;
        joined.push(...(index === 0 ? segment : segment.slice(1)));
      });
      return joined;
    }

    function genStablePath(sx, sy, ex, ey, curv, n, stableSeed) {
      const savedSeed = seed;
      seed = stableSeed || 1;
      const pts = genPath(sx, sy, ex, ey, curv, n);
      seed = savedSeed;
      return pts;
    }

    // --- Generate organic flowing path ---
    // Uses anchor interpolation for smooth, hand-drawn curves
    function genPath(sx, sy, ex, ey, curv, n) {
      const pts = [];
      const nA = 5 + Math.floor(sr() * 4);
      const anchY = [], anchX = [];
      for (let i = 0; i <= nA; i++) {
        const t = i / nA;
        const env = Math.sin(t * Math.PI) * (0.6 + 0.4 * Math.sin(t * Math.PI * 3 + sr() * 4));
        anchY.push((sr() - 0.5) * curv * env);
        anchX.push((sr() - 0.5) * curv * env * 0.3);
      }
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const bx = sx + (ex - sx) * t;
        const by = sy + (ey - sy) * t;
        const ap = t * nA;
        const ai = Math.min(Math.floor(ap), nA - 1);
        const af = ap - ai;
        const oy = anchY[ai] * (1 - af) + anchY[ai + 1] * af;
        const ox = anchX[ai] * (1 - af) + anchX[ai + 1] * af;
        pts.push({ x: bx + ox, y: by + oy });
      }
      return pts;
    }

    // --- Draw smooth Bezier curve with optional taper ---
    function drawCurve(pts, color, lw, alpha, fadeTail) {
      if (pts.length < 2) return;
      ctx.globalAlpha = alpha != null ? alpha : 1;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (fadeTail) {
        // Keep stroke weight consistent, but let the tail drift out in opacity.
        for (let i = 0; i < pts.length - 1; i++) {
          const t = i / (pts.length - 1);
          const a = (alpha != null ? alpha : 1) * (1 - t * 0.65);
          ctx.beginPath();
          ctx.strokeStyle = color;
          ctx.lineWidth = lw;
          ctx.globalAlpha = a;
          ctx.moveTo(pts[i].x, pts[i].y);
          ctx.lineTo(pts[i + 1].x, pts[i + 1].y);
          ctx.stroke();
        }
      } else {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = lw;
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length - 1; i++) {
          const mx = (pts[i].x + pts[i + 1].x) / 2;
          const my = (pts[i].y + pts[i + 1].y) / 2;
          ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
        }
        ctx.quadraticCurveTo(
          pts[pts.length - 2].x, pts[pts.length - 2].y,
          pts[pts.length - 1].x, pts[pts.length - 1].y
        );
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // Draw partial curve (for animation)
    function drawCurvePartial(pts, color, lw, alpha, progress) {
      if (pts.length < 2 || progress <= 0) return;
      const endIdx = Math.min(Math.ceil(progress * pts.length), pts.length);
      drawCurve(pts.slice(0, endIdx), color, lw, alpha);
    }

    function arrowHead(x, y, dir) {
      ctx.beginPath();
      ctx.moveTo(x, y); ctx.lineTo(x - dir * 9, y - 5);
      ctx.moveTo(x, y); ctx.lineTo(x - dir * 9, y + 5);
      ctx.stroke();
    }

    function drawTextBlock(x, y, title, lines, options = {}) {
      const {
        align = 'center',
        titleColor = 'rgba(34,34,34,0.72)',
        bodyColor = 'rgba(85,85,85,0.56)',
        titleFont = '600 13px Arial',
        bodyFont = '11px Arial',
        lineHeight = 14,
      } = options;

      ctx.textAlign = align;
      ctx.fillStyle = titleColor;
      ctx.font = titleFont;
      ctx.fillText(title, x, y);

      ctx.fillStyle = bodyColor;
      ctx.font = bodyFont;
      lines.forEach((line, index) => {
        ctx.fillText(line, x, y + 18 + index * lineHeight);
      });
    }

    function closestPathPoint(pathList, targetX, targetY, xWindow = 28) {
      let best = null;
      pathList.forEach((path) => {
        path.pts.forEach((pt) => {
          const dx = Math.abs(pt.x - targetX);
          if (dx > xWindow) return;
          const dy = Math.abs(pt.y - targetY);
          const score = dx * 0.8 + dy;
          if (!best || score < best.score) {
            best = { x: pt.x, y: pt.y, score };
          }
        });
      });
      return best;
    }

    function generatePastField(currentNode, decisionNodes) {
      if (currentNode.x - birthX < 36) return [];

      const softenedDecisionNodes = decisionNodes.map((node, idx) => {
        const t = decisionNodes.length > 1 ? idx / (decisionNodes.length - 1) : 0;
        const structuralY = CY + Math.sin(idx * 1.35 + 0.7) * 12;
        const followWeight = 0.56 + t * 0.18;
        return {
          x: node.x,
          y: structuralY * (1 - followWeight) + node.y * followWeight,
        };
      });

      const anchors = [{ x: birthX, y: CY }, ...softenedDecisionNodes, { x: currentNode.x, y: currentNode.y }]
        .filter((anchor, index, all) => {
          if (index === 0) return true;
          const prev = all[index - 1];
          return Math.abs(anchor.x - prev.x) > 8 || Math.abs(anchor.y - prev.y) > 8;
        });

      if (anchors.length < 2) return [];

      const span = Math.max(1, currentNode.x - birthX);
      const strandCount = Math.max(10, Math.min(18, 10 + decisionNodes.length * 2));
      const strands = [];
      const columns = anchors.map(() => []);

      for (let i = 0; i < strandCount; i++) {
        const mid = (strandCount - 1) / 2 || 1;
        let lane = (i - mid) / mid;
        let currentPt = anchors[0];
        let pts = [{ x: currentPt.x, y: currentPt.y }];
        columns[0].push(currentPt);

        for (let segIdx = 1; segIdx < anchors.length; segIdx++) {
          const prevAnchor = anchors[segIdx - 1];
          const nextAnchor = anchors[segIdx];
          const progress = (nextAnchor.x - birthX) / span;
          const segSeed = 7000 + i * 97 + segIdx * 131;
          const wobble = (((segSeed % 29) / 28) - 0.5) * 0.85;
          const spread = Math.max(18, 132 - progress * 62 + (anchors.length - segIdx) * 6);
          lane = lane * (0.78 - progress * 0.14) + wobble * (0.24 + (1 - progress) * 0.12);
          if ((i + segIdx) % 4 === 0) lane *= 0.72;

          const isFinalSegment = segIdx === anchors.length - 1;
          const endCenterY = currentNode.y;
          const endSpread = Math.max(54, 86 - decisionNodes.length * 2);
          const targetY = Math.max(
            74,
            Math.min(
              H - 55,
              isFinalSegment
                ? endCenterY + lane * endSpread + wobble * 12
                : nextAnchor.y + lane * spread
            )
          );
          const midX = prevAnchor.x + (nextAnchor.x - prevAnchor.x) * (0.42 + ((segSeed % 7) / 6) * 0.18);
          const midY = Math.max(
            74,
            Math.min(
              H - 55,
              (currentPt.y + targetY) * 0.5 + wobble * (isFinalSegment ? endSpread : spread) * 0.3
            )
          );
          const segment = combineSegments(
            genStablePath(currentPt.x, currentPt.y, midX, midY, 18 + segIdx * 2, 9, segSeed),
            genStablePath(midX, midY, nextAnchor.x, targetY, 14 + segIdx * 2, 10, segSeed + 43)
          );

          pts = combineSegments(pts, segment);
          currentPt = { x: nextAnchor.x, y: targetY };
          columns[segIdx].push(currentPt);
        }

        strands.push({
          pts,
          fadeTail: false,
          alpha: 0.17 + (i % 3 === 0 ? 0.12 : 0.04),
          lw: PATH_LINE_WIDTH,
        });
      }

      for (let segIdx = 1; segIdx < columns.length - 1; segIdx++) {
        const column = columns[segIdx];
        for (let i = 0; i < column.length - 2; i += 3) {
          const a = column[i];
          const b = column[Math.min(column.length - 1, i + 2)];
          const connectorSeed = 11000 + segIdx * 157 + i * 19;
          strands.push({
            pts: genStablePath(a.x, a.y, b.x, b.y, 10, 5, connectorSeed),
            fadeTail: false,
            alpha: 0.11,
            lw: PATH_LINE_WIDTH,
          });
        }
      }

      const finalColumn = columns[columns.length - 1];
      for (let i = 0; i < finalColumn.length - 2; i += 4) {
        const a = finalColumn[i];
        const b = finalColumn[Math.min(finalColumn.length - 1, i + 2)];
        const connectorSeed = 15000 + i * 23;
        strands.push({
          pts: genStablePath(a.x, a.y, b.x, b.y, 8, 4, connectorSeed),
          fadeTail: false,
          alpha: 0.08,
          lw: PATH_LINE_WIDTH,
        });
      }

      return strands;
    }

    // --- State ---
    let phase = 'intro';
    let time = 0;
    let currentX = birthX;
    let currentY = CY;
    let greenPoints = [{ x: birthX, y: CY }]; // the actual green path points
    let blackPaths = []; // dynamic past field
    let decisionNodes = [];
    let choiceIndex = 0;
    let remainingChoices = [...LIFE_CHOICES];
    let forkA = [], forkB = [];
    let forkAnimProgress = 0;
    let chosenOption = null;
    let introTimer = 0;
    let rafId = null;

    const numChoices = Math.min(LIFE_CHOICES.length, 12);
    // Increasing spacing — tight at start, wider later (growing agency)
    function getChoiceX(idx) {
      // Quadratic spacing: closer at start, wider at end
      const t = idx / numChoices;
      const usableWidth = presentMaxX - birthX - 60;
      return birthX + 40 + usableWidth * (t * 0.6 + t * t * 0.4);
    }

    function getRandomChoice() {
      if (remainingChoices.length === 0) return null;
      const idx = Math.floor(Math.random() * remainingChoices.length);
      return remainingChoices.splice(idx, 1)[0];
    }

    function rebuildPastField() {
      blackPaths = generatePastField({ x: currentX, y: currentY }, decisionNodes);
    }

    function getDisplayGreenPath() {
      if (greenPoints.length < 4) return greenPoints;
      const smoothStartX = presentMaxX - 120;
      return greenPoints.map((pt, idx) => {
        if (idx === 0 || idx >= greenPoints.length - 2 || pt.x < smoothStartX) return pt;
        const prev = greenPoints[idx - 1];
        const next = greenPoints[idx + 1];
        const t = Math.min(1, Math.max(0, (pt.x - smoothStartX) / 120));
        const smoothedY = (prev.y + pt.y * 2 + next.y) / 4;
        return {
          x: pt.x,
          y: pt.y * (1 - t * 0.42) + smoothedY * (t * 0.42),
        };
      });
    }

    // --- Generate future green fan (interwoven, not just branching) ---
    function generateFutureFan(fromX, fromY, toX, spread, count) {
      const paths = [];
      const ss = seed;
      seed = (Math.floor(fromX * 17 + fromY * 11) % 2147483647) || 1;
      const fanCenterY = fromY * 0.58 + CY * 0.42;
      const startBand = 42;
      const gatewayNodes = Array.from({ length: 6 }, (_, idx) => ({
        x: fromX + 18 + idx * 12 + sr() * 12,
        y: Math.max(75, Math.min(H - 55, fromY + (idx - 2.5) * 12 + (sr() - 0.5) * 10)),
      }));

      const endpoints = [];
      for (let i = 0; i < count; i++) {
        const norm = (i - (count - 1) / 2) / ((count - 1) / 2 || 1);
        const skew = Math.sign(norm) * Math.pow(Math.abs(norm), 0.75);
        let endY = fanCenterY + skew * spread * (0.92 + Math.abs(norm) * 0.14);
        endY = Math.max(70, Math.min(H - 55, endY));
        endpoints.push(endY);
        const gateway = gatewayNodes[Math.max(0, Math.min(gatewayNodes.length - 1, Math.round((norm + 1) * 0.5 * (gatewayNodes.length - 1))))];
        const stemX = fromX + 10 + sr() * 8;
        const stemY = Math.max(75, Math.min(H - 55, fromY + norm * startBand * 0.35 + (sr() - 0.5) * 5));
        const exitGate = combineSegments(
          genPath(fromX, fromY, stemX, stemY, 4 + sr() * 3, 4),
          genPath(stemX, stemY, gateway.x, gateway.y, 6 + sr() * 4, 4)
        );
        const branchX = gateway.x + 26 + sr() * 20;
        const branchY = gateway.y + (endY - gateway.y) * (0.16 + sr() * 0.1);
        const pts = combineSegments(
          exitGate,
          genPath(gateway.x, gateway.y, branchX, branchY, 8 + sr() * 4, 5),
          genPath(branchX, branchY, toX, endY, 26 + sr() * 16, 18)
        );
        paths.push(pts);
      }

      // Sub-branches that reconnect / interweave
      for (let i = 0; i < count - 1; i += 3) {
        const parent = paths[i];
        const bIdx = 10 + Math.floor(sr() * 8);
        if (bIdx >= parent.length) continue;
        const sp = parent[bIdx];
        // Branch toward a neighboring path's endpoint area
        const neighborEndY = endpoints[Math.min(i + 1, count - 1)];
        const bEndY = Math.max(70, Math.min(H - 55, sp.y + (neighborEndY - sp.y) * 0.34 + (sr() - 0.5) * 22));
        const bEndX = sp.x + (toX - sp.x) * (0.38 + sr() * 0.24);
        paths.push(genPath(sp.x, sp.y, bEndX, bEndY, 20, 12));
      }

      // Cross-connections between future paths
      for (let i = 0; i < count - 1; i += 6) {
        const p1 = paths[i];
        const p2 = paths[Math.min(i + 2, paths.length - 1)];
        const midIdx = 12 + Math.floor(sr() * 4);
        const endIdx = Math.min(p2.length - 1, midIdx + 3 + Math.floor(sr() * 3));
        if (midIdx < p1.length && endIdx < p2.length) {
          paths.push(genPath(p1[midIdx].x, p1[midIdx].y, p2[endIdx].x, p2[endIdx].y, 8, 4));
        }
      }

      seed = ss;
      return paths;
    }

    // --- Choice UI ---
    function showChoiceUI(choice) {
      return new Promise((resolve) => {
        const orderedOptions = Math.random() < 0.5
          ? [
              { key: 'a', label: choice.a, arrow: '↗', accent: 'green' },
              { key: 'b', label: choice.b, arrow: '↘', accent: 'neutral' },
            ]
          : [
              { key: 'b', label: choice.b, arrow: '↘', accent: 'neutral' },
              { key: 'a', label: choice.a, arrow: '↗', accent: 'green' },
            ];

        overlay.innerHTML = `
          <div style="
            width:min(100%, 720px); margin:0 auto;
            background:rgba(255,255,255,0.94); backdrop-filter:blur(4px);
            border:1.5px solid rgba(0,0,0,0.16); border-radius:16px;
            padding:16px 20px;
            box-shadow:0 8px 28px rgba(0,0,0,0.08);
            font-family:Arial,sans-serif;
          ">
            <div style="font-size:14px;font-weight:600;text-align:center;color:#333;margin-bottom:12px;line-height:1.4;">
              ${choice.question}
            </div>
            <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;text-align:center;color:#8a8a8a;margin-bottom:10px;">
              Make a choice today
            </div>
            <div style="display:flex;gap:10px;justify-content:center;">
              ${orderedOptions.map((option, index) => `
                <button id="choice${index}" data-key="${option.key}" data-accent="${option.accent}" style="
                  flex:1;padding:11px 14px;font-size:12px;
                  background:${option.accent === 'green' ? 'rgba(0,204,0,0.06)' : 'rgba(0,0,0,0.03)'};
                  border:1.5px solid ${option.accent === 'green' ? '#00CC00' : '#888'};
                  border-radius:10px;cursor:pointer;font-weight:500;
                  color:${option.accent === 'green' ? '#1a8a1a' : '#333'};transition:all 0.2s;
                ">${option.arrow} ${option.label}</button>
              `).join('')}
            </div>
          </div>
        `;
        orderedOptions.forEach((option, index) => {
          const button = document.getElementById(`choice${index}`);
          if (!button) return;
          button.onclick = () => { overlay.innerHTML = ''; resolve(option.key); };
          button.onmouseenter = () => {
            button.style.background = option.accent === 'green' ? '#00CC00' : '#333';
            button.style.color = '#fff';
          };
          button.onmouseleave = () => {
            button.style.background = option.accent === 'green' ? 'rgba(0,204,0,0.06)' : 'rgba(0,0,0,0.03)';
            button.style.color = option.accent === 'green' ? '#1a8a1a' : '#333';
          };
        });
      });
    }

    // --- Current future paths ---
    let futureFan = [];
    function updateFutureFan() {
      futureFan = generateFutureFan(currentX, currentY, maxX, 230, 12);
    }

    // --- Draw everything ---
    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#FFF';
      ctx.fillRect(0, 0, W, H);

      // Legend (subtle, top center)
      ctx.fillStyle = '#000';
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'left';
      ctx.fillRect(W / 2 - 125, 9, 10, 10);
      ctx.fillText('= LIFE PATHS CLOSED TO YOU', W / 2 - 110, 18);
      ctx.fillStyle = '#00CC00';
      ctx.fillRect(W / 2 - 125, 24, 10, 10);
      ctx.fillStyle = '#000';
      ctx.fillText('= LIFE PATHS OPEN TO YOU', W / 2 - 110, 33);

      // NOW dashed line
      if (phase !== 'intro') {
        const nx = Math.min(currentX, maxX);
        ctx.fillStyle = '#555';
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(phase === 'ended' ? 'TODAY' : 'NOW', nx, 52);
        ctx.beginPath();
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.moveTo(nx, 57);
        ctx.lineTo(nx, H - 35);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // --- Black paths (the web of closed possibilities) ---
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, Math.min(currentX, maxX) + 2, H);
      ctx.clip();
      blackPaths.forEach(p => {
        drawCurve(p.pts, '#000', p.lw || PATH_LINE_WIDTH, p.alpha || 0.8, p.fadeTail);
      });
      ctx.restore();

      // --- Future fan (green, interwoven) ---
      if (phase !== 'intro' && phase !== 'ended') {
        futureFan.forEach(p => drawCurve(p, '#00CC00', PATH_LINE_WIDTH, 0.28));
      }
      if (phase === 'ended') {
        const endFan = generateFutureFan(currentX, currentY, maxX, 245, 12);
        endFan.forEach(p => drawCurve(p, '#00CC00', PATH_LINE_WIDTH, 0.64));
      }

      // --- Green path (your life — drawn on top) ---
      if (greenPoints.length > 1) {
        drawCurve(getDisplayGreenPath(), '#00CC00', PATH_LINE_WIDTH, 1);
      }

      // --- Fork animation ---
      if (phase === 'animating' && forkA.length > 0) {
        const cFork = chosenOption === 'a' ? forkA : forkB;
        const xFork = chosenOption === 'a' ? forkB : forkA;
        drawCurvePartial(cFork, '#00CC00', PATH_LINE_WIDTH, 1, forkAnimProgress);
        drawCurvePartial(xFork, '#000', PATH_LINE_WIDTH, 0.38 * (1 - forkAnimProgress * 0.3), forkAnimProgress);
      }

      // --- Fork preview (soft, emerging from messy field) ---
      if (phase === 'choosing' && forkA.length > 0) {
        // Soft glow of possibilities
        drawCurve(forkA, '#00CC00', PREVIEW_LINE_WIDTH, 0.18 + 0.08 * Math.sin(time * 0.06));
        drawCurve(forkB, '#00CC00', PREVIEW_LINE_WIDTH, 0.18 + 0.08 * Math.sin(time * 0.06 + 1));
      }

      // --- Birth dot ---
      ctx.fillStyle = '#00CC00';
      ctx.beginPath(); ctx.arc(birthX, CY, 7, 0, Math.PI * 2); ctx.fill();

      // "you're born"
      ctx.fillStyle = '#000';
      ctx.font = 'italic 11px Arial';
      ctx.textAlign = 'center';
      ctx.fillText("you're", birthX - 5, CY - 42);
      ctx.fillText("born", birthX - 5, CY - 29);
      ctx.strokeStyle = '#000'; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(birthX - 2, CY - 24); ctx.lineTo(birthX, CY - 10); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(birthX - 3, CY - 14); ctx.lineTo(birthX, CY - 10); ctx.lineTo(birthX + 3, CY - 14);
      ctx.stroke();

      // "your life path"
      ctx.font = 'italic 10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('your', birthX - 30, CY + 48);
      ctx.fillText('life path', birthX - 30, CY + 60);
      ctx.strokeStyle = '#000'; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(birthX + 10, CY + 42); ctx.lineTo(birthX + 30, CY + 14); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(birthX + 25, CY + 18); ctx.lineTo(birthX + 30, CY + 14); ctx.lineTo(birthX + 24, CY + 12);
      ctx.stroke();

      // Current position dot
      if (phase !== 'intro') {
        ctx.fillStyle = '#00CC00';
        ctx.beginPath(); ctx.arc(currentX, currentY, 5, 0, Math.PI * 2); ctx.fill();
      }

      // "your life, today" (only during pause or end)
      if (phase === 'choosing' || phase === 'ended') {
        ctx.fillStyle = 'rgba(85,85,85,0.78)';
        ctx.font = 'italic 10px Arial';
        ctx.textAlign = 'left';
        const ly = currentY + 25;
        const lx = Math.min(W - 72, currentX + 12);
        ctx.fillText('your life,', lx, ly);
        ctx.fillText('today', lx, ly + 12);
        ctx.strokeStyle = 'rgba(85,85,85,0.55)'; ctx.lineWidth = 0.7;
        ctx.beginPath(); ctx.moveTo(lx + 2, ly - 6); ctx.lineTo(currentX + 3, currentY + 8); ctx.stroke();
      }

      // --- Bottom labels ---
      const by = H - 12;
      ctx.fillStyle = '#000';
      ctx.font = 'bold 13px Arial';
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#000'; ctx.lineWidth = 1.5;

      if (currentX > birthX + 90) {
        const pcx = (birthX + currentX) / 2;
        ctx.fillText('THE PAST', pcx, by);
        const aw = Math.min(55, (currentX - birthX) / 2 - 28);
        if (aw > 10) {
          ctx.beginPath(); ctx.moveTo(pcx - aw, by - 12); ctx.lineTo(pcx + aw, by - 12); ctx.stroke();
          arrowHead(pcx - aw, by - 12, -1); arrowHead(pcx + aw, by - 12, 1);
        }
      }
      if (currentX < maxX - 90) {
        const fcx = (currentX + maxX) / 2 + 8;
        ctx.fillText('THE FUTURE', fcx, by);
        const aw = Math.min(55, (maxX - currentX) / 2 - 32);
        if (aw > 10) {
          ctx.beginPath(); ctx.moveTo(fcx - aw, by - 12); ctx.lineTo(fcx + aw, by - 12); ctx.stroke();
          arrowHead(fcx - aw, by - 12, -1); arrowHead(fcx + aw, by - 12, 1);
        }
      }

      // Choice counter (subtle)
      if (choiceIndex > 0) {
        ctx.fillStyle = '#bbb'; ctx.font = '10px Arial'; ctx.textAlign = 'right';
        ctx.fillText(`${choiceIndex} choices made`, W - 10, 14);
      }

      // End message (subtle, integrated)
      if (phase === 'ended') {
        const topBlockX = Math.max(300, currentX - 150);
        const topBlockY = 110;
        const tx = currentX + (maxX - currentX) * 0.24;
        const ty = H * 0.73;
        drawTextBlock(
          topBlockX,
          topBlockY,
          'A Reading Of The Diagram',
          [
            'The past is fixed now.',
            'Many possible lives collapsed into one lived reality.',
            'From today, the future opens again.'
          ],
          { bodyColor: 'rgba(95,95,95,0.5)' }
        );

        drawTextBlock(
          tx,
          ty,
          'The choices were always yours to make.',
          [
            `${choiceIndex} choices passed through your hands and mind.`,
            'Some closed paths. Some kept options alive.',
            'But the outcomes were never fully yours to control.',
            'Steer the process, not the destination.'
          ],
          {
            titleFont: '600 16px Arial',
            bodyFont: '12px Arial',
            lineHeight: 16,
            titleColor: 'rgba(34,34,34,0.68)',
            bodyColor: 'rgba(85,85,85,0.58)',
          }
        );
      }
    }

    // --- Intro ---
    overlay.innerHTML = `
      <div style="
        width:min(100%, 720px); margin:0 auto;
        text-align:center;background:rgba(255,255,255,0.95);padding:18px 24px;
        border-radius:14px;border:1.5px solid rgba(0,204,0,0.28);
        font-family:Arial,sans-serif;
        box-shadow:0 6px 24px rgba(0,0,0,0.05);
      ">
        <div style="font-size:18px;font-weight:600;margin-bottom:6px;color:#222;">Every choice shapes your path.</div>
        <div style="font-size:12px;color:#777;">Time only moves forward. You can never go back.</div>
        <div style="font-size:10px;color:#aaa;margin-top:6px;">Inspired by life-advice and philosophy discussions.</div>
      </div>
    `;

    function frame() {
      return new Promise(resolve => { rafId = requestAnimationFrame(() => resolve()); });
    }

    async function gameLoop() {
      // Intro
      phase = 'intro';
      introTimer = 0;
      while (introTimer < 150) {
        introTimer++; time++;
        draw();
        await frame();
      }
      overlay.innerHTML = '';
      phase = 'playing';
      rebuildPastField();
      updateFutureFan();

      let nextChoiceX = getChoiceX(0);

      while (phase !== 'ended') {
        time++;

        if (phase === 'playing') {
          // Green path grows with organic wandering
          currentX += 0.9;
          // Wandering Y: slow sine drift + small noise = hesitant, human feel
          const drift = Math.sin(time * 0.015) * 0.8 + Math.sin(time * 0.037) * 0.5;
          currentY += drift + (sr() - 0.5) * 0.8;
          const nearbyClosedPath = closestPathPoint(blackPaths, currentX, currentY, 26);
          if (nearbyClosedPath && nearbyClosedPath.score < 38 && sr() > 0.28) {
            currentY += (nearbyClosedPath.y - currentY) * (0.025 + Math.max(0, 0.08 - nearbyClosedPath.score * 0.0015));
          }
          const settleToCenter = Math.max(0, (currentX - (presentMaxX - 150)) / 150);
          if (settleToCenter > 0) {
            currentY += (CY - currentY) * 0.035 * settleToCenter;
          }
          currentY = Math.max(80, Math.min(H - 60, currentY));
          rebuildPastField();
          greenPoints.push({ x: currentX, y: currentY });

          // Refresh future fan gently
          if (time % 40 === 0) updateFutureFan();

          // Time for a choice?
          if (currentX >= nextChoiceX && choiceIndex < numChoices) {
            phase = 'choosing';
            const choice = getRandomChoice();
            if (!choice) {
              phase = 'ended';
              continue;
            }
            const lp = greenPoints[greenPoints.length - 1];
            rebuildPastField();

            // Fork length varies (tighter early, wider later = growing agency)
            const desiredForkLen = 40 + choiceIndex * 8 + sr() * 20;
            const maxForkLen = Math.max(28, presentMaxX - lp.x - 18);
            const forkLen = Math.min(desiredForkLen, maxForkLen);

            // Fork spread — not a clean split, more organic emergence
            const baseSpread = 30 + choiceIndex * 5 + sr() * 40;
            const asymmetry = (sr() - 0.5) * 20; // forks aren't symmetric
            const eYA = Math.max(75, Math.min(H - 55, lp.y - baseSpread + asymmetry));
            const eYB = Math.max(75, Math.min(H - 55, lp.y + baseSpread + asymmetry));

            forkA = genPath(lp.x, lp.y, lp.x + forkLen, eYA, 25 + sr() * 15, 18);
            forkB = genPath(lp.x, lp.y, lp.x + forkLen, eYB, 25 + sr() * 15, 18);

            updateFutureFan();
            draw();

            const chosen = await showChoiceUI(choice);
            chosenOption = chosen;
            phase = 'animating';
            forkAnimProgress = 0;

            while (forkAnimProgress < 1) {
              forkAnimProgress += 0.045;
              forkAnimProgress = Math.min(forkAnimProgress, 1);
              time++;
              draw();
              await frame();
            }

            // Finalize
            const cFork = chosenOption === 'a' ? forkA : forkB;
            greenPoints = greenPoints.concat(cFork.slice(1));
            decisionNodes.push({ x: lp.x, y: lp.y });

            const last = cFork[cFork.length - 1];
            currentX = last.x;
            currentY = last.y;
            choiceIndex++;
            forkA = []; forkB = [];
            nextChoiceX = getChoiceX(choiceIndex);
            rebuildPastField();
            updateFutureFan();

            if (choiceIndex >= numChoices || currentX >= presentMaxX - 8) {
              phase = 'ended';
            } else {
              phase = 'playing';
            }
          }

          if (currentX >= presentMaxX) phase = 'ended';
        }

        if (phase === 'choosing' || phase === 'ended') rebuildPastField();
        draw();
        await frame();
      }

      // Final draw
      draw();
    }

    gameLoop();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      overlay.innerHTML = '';
    };
  }, []);

  return (
    <div style={{
      width: '100vw', minHeight: '100vh',
      backgroundColor: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '18px 12px',
    }}>
      <div style={{
        width: 'min(95vw, 900px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
      }}>
        <canvas ref={canvasRef} width={900} height={520}
          style={{ border: '1px solid #eee', display: 'block', maxWidth: '100%', width: '100%', height: 'auto' }} />
        <div ref={overlayRef} style={{
          width: '100%',
          minHeight: '122px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }} />
      </div>
    </div>
  );
};

export default LifePathsFlow;
